/**
 * en / fr / es strings for the map-fields step (`MapFields.tsx`). The field labels are also the
 * `ryersonColumn` values in `BulkUploadModal.tsx` → `defaultFileData`, and the text the auto-match
 * looks for in a file's headers when no mapping is saved, so changing one changes which column is
 * picked automatically.
 *
 * Not read by any file: `saveThisFieldMapping`, `mapFields`.
 */
import { LanguageContent } from "@ryerson/frontend.common";

/** Shape of each language's strings in {@link MapFieldsContent}. */
export interface MapFieldsText {
	pleaseMatch: string;
	fieldsInFile: string;
	ourFields: string;
	partNumber: string;
	quantity: string;
	quantityUom: string;
	pricingUom: string;
	customerRefNumber: string;
	lineLevelPO: string;
	lineItemNotes: string;
	deliveryDate: string;
	saveThisFieldMapping: string;
	mapFields: string;
	isMissingRequiredData: string;
	thereWasAProblem: string;
	saveMapping: string;
}

/** The map-fields step's strings by language. */
export const MapFieldsContent: LanguageContent<MapFieldsText> = {
	en: {
		pleaseMatch: "Please match the fields in your file to the required fields.",
		fieldsInFile: "Fields in Your File",
		ourFields: "Our Columns",
		partNumber: "Part Number",
		quantity: "Quantity",
		quantityUom: "Quantity Unit of Measure",
		pricingUom: "Pricing Unit of Measure",
		customerRefNumber: "Customer Reference Number",
		lineLevelPO: "Line Level PO Number",
		lineItemNotes: "Line Item Notes",
		deliveryDate: "Future Delivery Date",
		saveThisFieldMapping: "save this field mapping for future uploads",
		mapFields: "Map the Fields and Review Items",
		isMissingRequiredData: "Required",
		thereWasAProblem: "There was a problem. Please try again later.",
		saveMapping: "Save this field mapping for future uploads",
	},
	fr: {
		pleaseMatch: "Veuillez faire correspondre les champs de votre fichier aux champs obligatoires.",
		fieldsInFile: "Champs de votre fichier",
		ourFields: "Nos colonnes",
		partNumber: "Numéro de pièce",
		quantity: "Quantité",
		quantityUom: "Quantité par unité de mesure",
		pricingUom: "Prix par unité de mesure",
		customerRefNumber: "Numéro de référence client",
		lineItemNotes: "Remarques sur le type d’article",
		lineLevelPO: "Numéro de BC de l’article",
		deliveryDate: "Date de livraison ultérieure",
		saveThisFieldMapping: "Enregistrer la correspondance des champs pour les prochains téléversements",
		mapFields: "Faire correspondre les champs et consulter les articles",
		isMissingRequiredData: "Champ obligatoire",
		thereWasAProblem: "Un problème est survenu. Veuillez réessayer plus tard.",
		saveMapping: "Enregistrer la correspondance des champs pour les prochains téléversements",
	},
	es: {
		pleaseMatch:
			"Haga coincidir los campos en su archivo con los campos requeridos.",
		fieldsInFile: "Campos en su archivo",
		ourFields: "Nuestras columnas",
		partNumber: "Número de pieza",
		quantity: "Cantidad",
		quantityUom: "Unidad de medida por cantidad",
		pricingUom: "Unidad de medida de precios",
		customerRefNumber: "Número de referencia del cliente",
		lineItemNotes: "Notas de artículos de línea",
		lineLevelPO: "Número de la orden de compra de nivel de línea",
		deliveryDate: "Fecha de entrega futura",
		saveThisFieldMapping: "guardar este mapeo de campos para cargas futuras",
		mapFields: "Mapear los campos y revisar artículos",
		isMissingRequiredData: "Obligatorio",
		thereWasAProblem: "Hubo un problema. Intente nuevamente más tarde",
		saveMapping: "guardar este mapeo de campos para cargas futuras",
	},
};
